import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import DashboardBase from "./DashboardBase";

const EmpleadosManager = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [empleados, setEmpleados] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [supervisores, setSupervisores] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroSupervisor, setFiltroSupervisor] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEmpleado, setEditingEmpleado] = useState(null);
  const [viewingEmpleado, setViewingEmpleado] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    cedula: "",
    nombre: "",
    apellido: "",
    email: "",
    telefono: "",
    cargo: "",
    departamento: "",
    supervisor_cedula: "",
    empresa_id: "",
    fecha_ingreso: "",
    estado: "activo"
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
        loadUserData(user);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadUserData = async (user) => {
    try {
      const q = query(collection(db, "usuarios"), where("uid", "==", user.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = { id: userDoc.id, ...userDoc.data() };
        setUserData(userData);
        
        await loadEmpresas(userData);
        await loadEmpleados(userData);
        await loadDepartamentos();
        await loadCargos();
      }
    } catch (error) {
      console.error("Error al cargar datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmpresas = async (userData) => {
    try {
      let empresasData = [];
      
      if (userData.rol === 'superadmin') {
        const empresasSnapshot = await getDocs(collection(db, "empresas"));
        empresasData = empresasSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } else {
        const empresaDoc = await getDocs(query(
          collection(db, "empresas"), 
          where("__name__", "==", userData.empresa_id)
        ));
        if (!empresaDoc.empty) {
          empresasData = [{ id: empresaDoc.docs[0].id, ...empresaDoc.docs[0].data() }];
        }
      }
      
      setEmpresas(empresasData);
      
      // Si solo hay una empresa, seleccionarla automáticamente
      if (empresasData.length === 1) {
        setFiltroEmpresa(empresasData[0].id);
        setFormData(prev => ({ ...prev, empresa_id: empresasData[0].id }));
        
        // Cargar departamentos y cargos de esta empresa
        await loadDepartamentos(empresasData[0].id);
        await loadCargos(empresasData[0].id);
      }
    } catch (error) {
      console.error("Error al cargar empresas:", error);
    }
  };

  const loadEmpleados = async (userData) => {
    try {
      let empleadosQuery;
      
      if (userData.rol === 'superadmin') {
        empleadosQuery = collection(db, "empleados");
      } else {
        empleadosQuery = query(
          collection(db, "empleados"),
          where("empresa_id", "==", userData.empresa_id)
        );
      }

      const unsubscribe = onSnapshot(empleadosQuery, (snapshot) => {
        const empleadosData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setEmpleados(empleadosData);
        
        // Cargar todos los empleados como posibles supervisores
        // Excluir el empleado actual si estamos editando
        setSupervisores(empleadosData);
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error al cargar empleados:", error);
    }
  };

  // Cargar departamentos desde Firebase
  const loadDepartamentos = async (empresaId = null) => {
    try {
      let departamentosQuery;
      
      if (empresaId) {
        departamentosQuery = query(
          collection(db, "departamentos"),
          where("empresaId", "==", empresaId)
        );
      } else {
        departamentosQuery = collection(db, "departamentos");
      }

      const querySnapshot = await getDocs(departamentosQuery);
      const departamentosData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDepartamentos(departamentosData);
      console.log("Departamentos cargados:", departamentosData);
    } catch (error) {
      console.error("Error al cargar departamentos:", error);
    }
  };

  // Cargar cargos desde Firebase
  const loadCargos = async (empresaId = null) => {
    try {
      let cargosQuery;
      
      if (empresaId) {
        cargosQuery = query(
          collection(db, "posiciones"),
          where("empresaId", "==", empresaId)
        );
      } else {
        cargosQuery = collection(db, "posiciones");
      }

      const querySnapshot = await getDocs(cargosQuery);
      const cargosData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setCargos(cargosData);
      console.log("Cargos cargados:", cargosData);
    } catch (error) {
      console.error("Error al cargar cargos:", error);
    }
  };

  // Manejar cambio de empresa (cargar también departamentos y cargos)
  const handleEmpresaChange = async (empresaId) => {
    setFiltroEmpresa(empresaId);
    
    // Cargar departamentos y cargos de la empresa seleccionada
    if (empresaId) {
      await loadDepartamentos(empresaId);
      await loadCargos(empresaId);
    } else {
      // Si no hay empresa seleccionada, cargar todos
      await loadDepartamentos();
      await loadCargos();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Si cambia la empresa en el formulario, actualizar departamentos y cargos
    if (name === 'empresa_id') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        // Limpiar cargo y departamento cuando cambia la empresa
        cargo: "",
        departamento: ""
      }));
      
      // Cargar departamentos y cargos de la nueva empresa
      if (value) {
        loadDepartamentos(value);
        loadCargos(value);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEmpleado) {
        // Actualizar empleado existente
        await updateDoc(doc(db, "empleados", editingEmpleado.id), {
          ...formData,
          fecha_actualizacion: new Date().toISOString()
        });
      } else {
        // Crear nuevo empleado
        await addDoc(collection(db, "empleados"), {
          ...formData,
          fecha_creacion: new Date().toISOString(),
          fecha_actualizacion: new Date().toISOString()
        });
      }
      
      resetForm();
    } catch (error) {
      console.error("Error al guardar empleado:", error);
      alert("Error al guardar empleado: " + error.message);
    }
  };

  const handleEdit = async (empleado) => {
    setEditingEmpleado(empleado);
    setViewingEmpleado(empleado);
    setIsEditMode(true);
    setFormData({
      cedula: empleado.cedula || "",
      nombre: empleado.nombre || "",
      apellido: empleado.apellido || "",
      email: empleado.email || "",
      telefono: empleado.telefono || "",
      cargo: empleado.cargo || "",
      departamento: empleado.departamento || "",
      supervisor_cedula: empleado.supervisor_cedula || "",
      empresa_id: empleado.empresa_id || "",
      fecha_ingreso: empleado.fecha_ingreso || "",
      estado: empleado.estado || "activo"
    });
    
    // Cargar departamentos y cargos de la empresa del empleado
    if (empleado.empresa_id) {
      await loadDepartamentos(empleado.empresa_id);
      await loadCargos(empleado.empresa_id);
    }
    
    setShowForm(true);
  };

  const handleView = async (empleado) => {
    setViewingEmpleado(empleado);
    setIsEditMode(false);
    setEditingEmpleado(null);
    
    // Cargar departamentos y cargos de la empresa del empleado para mostrar información completa
    if (empleado.empresa_id) {
      await loadDepartamentos(empleado.empresa_id);
      await loadCargos(empleado.empresa_id);
    }
    
    setShowForm(true);
  };

  const closeModal = () => {
    setShowForm(false);
    setViewingEmpleado(null);
    setEditingEmpleado(null);
    setIsEditMode(false);
    setFormData({
      cedula: "",
      nombre: "",
      apellido: "",
      email: "",
      telefono: "",
      cargo: "",
      departamento: "",
      supervisor_cedula: "",
      empresa_id: "",
      fecha_ingreso: "",
      estado: "activo"
    });
  };

  const handleDelete = async (empleado) => {
    const confirmMessage = `¿Está seguro de que desea eliminar al empleado?

Nombre: ${empleado.nombre} ${empleado.apellido}
Cédula: ${empleado.cedula}
Cargo: ${empleado.cargo}

Esta acción no se puede deshacer.`;

    if (window.confirm(confirmMessage)) {
      try {
        await deleteDoc(doc(db, "empleados", empleado.id));
        console.log("✅ Empleado eliminado:", empleado.nombre, empleado.apellido);
      } catch (error) {
        console.error("Error al eliminar empleado:", error);
        alert("Error al eliminar empleado: " + error.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      cedula: "",
      nombre: "",
      apellido: "",
      email: "",
      telefono: "",
      cargo: "",
      departamento: "",
      supervisor_cedula: "",
      empresa_id: empresas.length === 1 ? empresas[0].id : "",
      fecha_ingreso: "",
      estado: "activo"
    });
    setEditingEmpleado(null);
    closeModal();
  };

  const getEmpresaNombre = (empresaId) => {
    const empresa = empresas.find(e => e.id === empresaId);
    return empresa ? empresa.nombre : "Sin empresa";
  };

  // Función para obtener supervisores disponibles (excluyendo el empleado actual si estamos editando)
  const getSupervisoresDisponibles = () => {
    let empleadosDisponibles = empleados;
    
    if (editingEmpleado) {
      // Si estamos editando, excluir el empleado actual de la lista de supervisores
      empleadosDisponibles = empleados.filter(emp => emp.cedula !== editingEmpleado.cedula);
    }
    
    // Ordenar alfabéticamente por nombre y apellido
    return empleadosDisponibles.sort((a, b) => {
      const nombreA = `${a.nombre} ${a.apellido}`.toLowerCase();
      const nombreB = `${b.nombre} ${b.apellido}`.toLowerCase();
      return nombreA.localeCompare(nombreB);
    });
  };

  const getSupervisorNombre = (cedulaSupervisor) => {
    const supervisor = empleados.find(e => e.cedula === cedulaSupervisor);
    return supervisor ? `${supervisor.nombre} ${supervisor.apellido}` : "Sin supervisor";
  };

  const empleadosFiltrados = empleados.filter(empleado => {
    const matchEmpresa = !filtroEmpresa || empleado.empresa_id === filtroEmpresa;
    const matchSupervisor = !filtroSupervisor || empleado.supervisor_cedula === filtroSupervisor;
    const matchBusqueda = !busqueda || 
      empleado.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
      empleado.apellido?.toLowerCase().includes(busqueda.toLowerCase()) ||
      empleado.cedula?.includes(busqueda) ||
      empleado.email?.toLowerCase().includes(busqueda.toLowerCase()) ||
      empleado.cargo?.toLowerCase().includes(busqueda.toLowerCase());
    
    return matchEmpresa && matchSupervisor && matchBusqueda;
  });

  if (loading) {
    return (
      <DashboardBase title="Cargando..." activeNav="empleados">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #1e40af',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Cargando empleados...</p>
        </div>
      </DashboardBase>
    );
  }

  return (
    <DashboardBase title="Gestión de Empleados" activeNav="empleados">
      <div style={{ padding: '1.5rem' }}>
        {/* Header con botón de agregar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, color: '#1e293b' }}>
            Empleados {userData?.rol === 'superadmin' ? '(Todas las empresas)' : ''}
          </h2>
          <button
            onClick={() => {
              setViewingEmpleado(null);
              setEditingEmpleado(null);
              setIsEditMode(false);
              setFormData({
                cedula: "",
                nombre: "",
                apellido: "",
                email: "",
                telefono: "",
                cargo: "",
                departamento: "",
                supervisor_cedula: "",
                empresa_id: empresas.length === 1 ? empresas[0].id : "",
                fecha_ingreso: "",
                estado: "activo"
              });
              setShowForm(true);
            }}
            style={{
              backgroundColor: '#1e40af',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            + Agregar Empleado
          </button>
        </div>

        {/* Filtros */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8fafc',
          borderRadius: '6px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Buscar:
            </label>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Nombre, cédula, email..."
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            />
          </div>

          {userData?.rol === 'superadmin' && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Empresa:
              </label>
              <select
                value={filtroEmpresa}
                onChange={(e) => handleEmpresaChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              >
                <option value="">Todas las empresas</option>
                {empresas.map(empresa => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Supervisor:
            </label>
            <select
              value={filtroSupervisor}
              onChange={(e) => setFiltroSupervisor(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            >
              <option value="">Todos los supervisores</option>
              {empleados
                .sort((a, b) => {
                  const nombreA = `${a.nombre} ${a.apellido}`.toLowerCase();
                  const nombreB = `${b.nombre} ${b.apellido}`.toLowerCase();
                  return nombreA.localeCompare(nombreB);
                })
                .map(supervisor => (
                  <option key={supervisor.cedula} value={supervisor.cedula}>
                    {supervisor.nombre} {supervisor.apellido}
                  </option>
                ))
              }
            </select>
          </div>
        </div>

        {/* Formulario de empleado */}
        {showForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0 }}>
                  {viewingEmpleado && !isEditMode ? (
                    `👁️ Ver Empleado: ${viewingEmpleado.nombre} ${viewingEmpleado.apellido}`
                  ) : editingEmpleado ? (
                    `✏️ Editar Empleado: ${editingEmpleado.nombre} ${editingEmpleado.apellido}`
                  ) : (
                    '➕ Agregar Nuevo Empleado'
                  )}
                </h3>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✕ Cerrar
                </button>
              </div>

              {viewingEmpleado && !isEditMode ? (
                // Modo Vista
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                    <strong>Información Personal</strong>
                    <div style={{ marginTop: '0.5rem' }}>
                      <p><strong>Cédula:</strong> {viewingEmpleado.cedula}</p>
                      <p><strong>Nombre:</strong> {viewingEmpleado.nombre} {viewingEmpleado.apellido}</p>
                      <p><strong>Email:</strong> {viewingEmpleado.email || 'No especificado'}</p>
                      <p><strong>Teléfono:</strong> {viewingEmpleado.telefono || 'No especificado'}</p>
                      <p><strong>Estado:</strong> 
                        <span style={{ 
                          marginLeft: '0.5rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          backgroundColor: viewingEmpleado.estado === 'activo' ? '#dcfce7' : '#fee2e2',
                          color: viewingEmpleado.estado === 'activo' ? '#166534' : '#991b1b'
                        }}>
                          {viewingEmpleado.estado}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                    <strong>Información Laboral</strong>
                    <div style={{ marginTop: '0.5rem' }}>
                      <p><strong>Cargo:</strong> {viewingEmpleado.cargo}</p>
                      <p><strong>Departamento:</strong> {viewingEmpleado.departamento || 'No especificado'}</p>
                      <p><strong>Supervisor:</strong> {viewingEmpleado.supervisor_cedula ? getSupervisorNombre(viewingEmpleado.supervisor_cedula) : 'Sin supervisor'}</p>
                      {userData?.rol === 'superadmin' && (
                        <p><strong>Empresa:</strong> {getEmpresaNombre(viewingEmpleado.empresa_id)}</p>
                      )}
                      <p><strong>Fecha de Ingreso:</strong> {viewingEmpleado.fecha_ingreso || 'No especificada'}</p>
                    </div>
                  </div>

                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditMode(true);
                        setEditingEmpleado(viewingEmpleado);
                        setFormData({
                          cedula: viewingEmpleado.cedula || "",
                          nombre: viewingEmpleado.nombre || "",
                          apellido: viewingEmpleado.apellido || "",
                          email: viewingEmpleado.email || "",
                          telefono: viewingEmpleado.telefono || "",
                          cargo: viewingEmpleado.cargo || "",
                          departamento: viewingEmpleado.departamento || "",
                          supervisor_cedula: viewingEmpleado.supervisor_cedula || "",
                          empresa_id: viewingEmpleado.empresa_id || "",
                          fecha_ingreso: viewingEmpleado.fecha_ingreso || "",
                          estado: viewingEmpleado.estado || "activo"
                        });
                      }}
                      style={{
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      ✏️ Editar Empleado
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        closeModal();
                        handleDelete(viewingEmpleado);
                      }}
                      style={{
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      🗑️ Eliminar Empleado
                    </button>
                  </div>
                </div>
              ) : (
                // Modo Formulario (Crear/Editar)
                <form onSubmit={handleSubmit}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem'
                }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Cédula: *
                    </label>
                    <input
                      type="text"
                      name="cedula"
                      value={formData.cedula}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Nombre: *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Apellido: *
                    </label>
                    <input
                      type="text"
                      name="apellido"
                      value={formData.apellido}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Email:
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Teléfono:
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Cargo: *
                    </label>
                    <select
                      name="cargo"
                      value={formData.cargo}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    >
                      <option value="">Selecciona un cargo</option>
                      {cargos.map(cargo => (
                        <option key={cargo.id} value={cargo.nombre}>
                          {cargo.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Departamento:
                    </label>
                    <select
                      name="departamento"
                      value={formData.departamento}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    >
                      <option value="">Selecciona un departamento</option>
                      {departamentos.map(departamento => (
                        <option key={departamento.id} value={departamento.nombre}>
                          {departamento.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Supervisor:
                    </label>
                    <select
                      name="supervisor_cedula"
                      value={formData.supervisor_cedula}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    >
                      <option value="">Sin supervisor</option>
                      {getSupervisoresDisponibles().map(supervisor => (
                        <option key={supervisor.cedula} value={supervisor.cedula}>
                          {supervisor.nombre} {supervisor.apellido} - {supervisor.cargo || 'Sin cargo'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {userData?.rol === 'superadmin' && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                        Empresa: *
                      </label>
                      <select
                        name="empresa_id"
                        value={formData.empresa_id}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px'
                        }}
                      >
                        <option value="">Seleccionar empresa</option>
                        {empresas.map(empresa => (
                          <option key={empresa.id} value={empresa.id}>
                            {empresa.nombre}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Fecha de Ingreso:
                    </label>
                    <input
                      type="date"
                      name="fecha_ingreso"
                      value={formData.fecha_ingreso}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Estado:
                    </label>
                    <select
                      name="estado"
                      value={formData.estado}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px'
                      }}
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                      <option value="suspendido">Suspendido</option>
                    </select>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '1rem',
                  marginTop: '2rem'
                }}>
                  <button
                    type="button"
                    onClick={resetForm}
                    style={{
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    style={{
                      backgroundColor: '#1e40af',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    {editingEmpleado ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        )}

        {/* Lista de empleados */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: userData?.rol === 'superadmin' 
              ? '150px 200px 150px 150px 200px 150px 160px' 
              : '150px 200px 150px 150px 200px 160px',
            gap: '1rem',
            padding: '1rem',
            backgroundColor: '#f9fafb',
            fontWeight: '600',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <div>Cédula</div>
            <div>Nombre Completo</div>
            <div>Cargo</div>
            <div>Departamento</div>
            <div>Supervisor</div>
            {userData?.rol === 'superadmin' && <div>Empresa</div>}
            <div>Acciones</div>
          </div>

          {empleadosFiltrados.length === 0 ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#6b7280'
            }}>
              No se encontraron empleados
            </div>
          ) : (
            empleadosFiltrados.map(empleado => (
              <div
                key={empleado.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: userData?.rol === 'superadmin' 
                    ? '150px 200px 150px 150px 200px 150px 160px' 
                    : '150px 200px 150px 150px 200px 160px',
                  gap: '1rem',
                  padding: '1rem',
                  borderBottom: '1px solid #f3f4f6',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  ':hover': {
                    backgroundColor: '#f9fafb'
                  }
                }}
                onClick={() => handleView(empleado)}
                title="Haz clic para ver detalles del empleado"
              >
                <div style={{ fontFamily: 'monospace' }}>{empleado.cedula}</div>
                <div>
                  <div style={{ fontWeight: '500' }}>
                    {empleado.nombre} {empleado.apellido}
                  </div>
                  {empleado.email && (
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {empleado.email}
                    </div>
                  )}
                </div>
                <div>{empleado.cargo}</div>
                <div>{empleado.departamento || '-'}</div>
                <div style={{ fontSize: '0.875rem' }}>
                  {empleado.supervisor_cedula ? getSupervisorNombre(empleado.supervisor_cedula) : 'Sin supervisor'}
                </div>
                {userData?.rol === 'superadmin' && (
                  <div style={{ fontSize: '0.875rem' }}>
                    {getEmpresaNombre(empleado.empresa_id)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(empleado);
                    }}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                    title="Ver detalles"
                  >
                    👁️ Ver
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(empleado);
                    }}
                    style={{
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                    title="Editar empleado"
                  >
                    ✏️ Editar
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(empleado);
                    }}
                    style={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                    title="Eliminar empleado"
                  >
                    🗑️ Eliminar
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Estadísticas */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginTop: '1.5rem'
        }}>
          <div style={{
            backgroundColor: '#dbeafe',
            padding: '1rem',
            borderRadius: '6px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e40af' }}>
              {empleadosFiltrados.length}
            </div>
            <div style={{ color: '#1e40af', fontSize: '0.875rem' }}>
              Total Empleados
            </div>
          </div>
          
          <div style={{
            backgroundColor: '#dcfce7',
            padding: '1rem',
            borderRadius: '6px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#16a34a' }}>
              {empleadosFiltrados.filter(e => e.estado === 'activo').length}
            </div>
            <div style={{ color: '#16a34a', fontSize: '0.875rem' }}>
              Activos
            </div>
          </div>
          
          <div style={{
            backgroundColor: '#fef3c7',
            padding: '1rem',
            borderRadius: '6px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#d97706' }}>
              {supervisores.length}
            </div>
            <div style={{ color: '#d97706', fontSize: '0.875rem' }}>
              Supervisores
            </div>
          </div>
        </div>
      </div>
    </DashboardBase>
  );
};

export default EmpleadosManager;
